import React, { useState, useMemo } from 'react';
import { Box, MuiBox, Button, FlexBox, Grid, InputField, Typography, SelectDropdown } from '@kis-technologies/kis-ui';
import { CreateKioskPayload } from 'store/kiosks';
import { getKioskInitialForm, kioskValidationSchema, getHasEmptyInput, getHasInvalidInput } from '../../utils';
import { useStyles } from '../../styled';
import { INVALID, VALID } from '../../constants'; // needs to be changed
import { ConfigCreateFormProps } from '../../types';
import { selectors as configSelectors, Configuration } from 'store/configurations';
import { selectors as CISelectors, ClientIntegration } from 'store/clientIntegrations';
import { actions as areasActions, selectors as areasSelectors, Area } from 'store/areas';
import { getSortedList } from 'utils/stringFormat';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';

export const CreateKioskForm = (props: ConfigCreateFormProps) => {
  const { loading, error, resetError, onSubmit } = props;
  const classes = useStyles()();

  const [getAreas] = useActions([areasActions.getAreas]);

  const configurations = useSelector(configSelectors.selectConfigurations);
  const clientIntegrations = useSelector(CISelectors.selectClientIntegrations);
  const areas = useSelector(areasSelectors.selectAreas);

  const [selectedConfiguration, setSelectedConfiguration] = useState<Configuration | null>(null);
  const [selectedCI, setSelectedCI] = useState<ClientIntegration | null>(null);
  const [selectedArea, setSelectedArea] = useState<Area | null>(null);

  // SETS THE FORM VALUES AND VALIDATION VALUES FOR EACH FORM VALUE (INVALID | VALID)
  const initialForm = getKioskInitialForm();
  const [form, setForm] = useState(initialForm);
  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});

  // CHECKS INVALIDATIONS AND EMPTY INPUTS AND INITIAL VALUES
  const hasEmptyInput = useMemo(() => getHasEmptyInput(form), [form]);
  const hasInvalidInput = useMemo(() => getHasInvalidInput(validation), [validation]);

  React.useEffect(() => {
    if (!areas || !areas.length) getAreas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FORM SUBMIT FOR CREATION
  const onCreateClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const payload: CreateKioskPayload = {
      name: form.name,
      areaId: form.areaId,
      configurationId: form.configurationId,
      clientIntegrationId: form.clientIntegrationId,
      macAddress: form.macAddress
    };

    onSubmit(payload);
  };

  const onInputChange = (key: keyof ReturnType<typeof getKioskInitialForm>, e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    resetError();
    // IF THERE WAS VALIDATION ERROR FOR SELECTED INPUT - RESET IT TO VALID
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }

    // UPDATE THE FORM OBJECT WITH THE CHANGE TO CORRESPONDING KEY VALUE PAIR
    setForm({ ...form, [key]: e.target.value });
  };

  // FORM INPUT BLUR
  const onInputBlur = async (
    key: keyof ReturnType<typeof getKioskInitialForm>,
    e: React.ChangeEvent<HTMLInputElement>,
    value: string | null = null
  ) => {
    e.preventDefault();

    // TRIMS WHITE SPACE OF THE INPUT VALUES ONBLUR
    setForm({ ...form, [key]: e.target.value.trim() });

    let invalidMessage = null;

    // IF THE TRIMMED STRING VALUE HAS VALUE RUN VALIDATION AND SET INVALID MESSAGE
    if (value && value.toString().trim().length) {
      const result = await kioskValidationSchema
        .validate({ [key]: value && value.toString().trim() })
        .catch((err) => err);
      invalidMessage = result && result.message;
    }

    // SET VALIDATION TO BE VALID OR INVALID DEPENDING ON WHETHER INVALID MESSAGE EXISTS
    setValidation({ ...validation, [key]: invalidMessage ? INVALID : VALID });
    setValidationMessage({ ...validationMessage, [key]: invalidMessage });
  };

  // FORM INPUT VALIDATION
  const validate = (key: keyof ReturnType<typeof getKioskInitialForm>) => {
    return validationMessage[key];
  };

  // CONDITIONS FOR DISABLING SUBMIT BUTTON
  const buttonDisabled = hasEmptyInput || hasInvalidInput;

  // for configuration selection
  const onConfigurationChange = (item: Configuration) => {
    setSelectedConfiguration(item);
    setForm({ ...form, configurationId: item.id });
  };
  // for client integration selection
  const onCIChange = (item: ClientIntegration) => {
    setSelectedCI(item);
    setForm({ ...form, clientIntegrationId: item.id });
  };

  // for area selection
  const onAreaChange = (item: Area) => {
    setSelectedArea(item);
    setForm({ ...form, areaId: item.id });
  };

  return (
    <FlexBox justify="center" flexOverride="1 1 0%" padding="0 0 11">
      <FlexBox direction="column" fullWidth align="center" justify="center">
        <Grid className={classes.createClientInnerContainer}>
          <Box flexOverride="none" fullWidth margin="0 0 4">
            <Typography className={classes.createNewClientHeading}>Create New Kiosk</Typography>
          </Box>
          {/* INPUT - NAME */}
          <MuiBox className={classes.createClientName}>
            <InputField
              label="name"
              size="medium"
              fullWidth
              placeholder="name"
              value={form.name}
              onChange={(e) => onInputChange('name', e)}
              onBlur={(e) => onInputBlur('name', e, e.target.value.trim())}
              helperText={validate('name')}
              error={validation.name === INVALID}
            />
          </MuiBox>
          <FlexBox fullWidth direction="row" align="center" justifyContent="space-between">
            <Typography fontSize={16} fontWeight={600} sx={{ padding: '0 0 0 24px' }}>
              ClientIntegration
            </Typography>
            <SelectDropdown
              placeholder="Select a ClientIntegration"
              items={getSortedList(clientIntegrations, 'name')}
              selectedItem={selectedCI as ClientIntegration}
              onChange={(item: ClientIntegration) => onCIChange(item)}
              itemToString={(item: ClientIntegration) => (item ? item.name : '')}
            />
          </FlexBox>
          <FlexBox fullWidth direction="row" margin="6 6 0" align="center" justifyContent="space-between">
            <Typography fontSize={16} fontWeight={600} sx={{ padding: '0 0 0 24px' }}>
              Configuration
            </Typography>
            <SelectDropdown
              placeholder="Select a Configuration"
              items={getSortedList(configurations, 'name')}
              selectedItem={selectedConfiguration as Configuration}
              onChange={(item: Configuration) => onConfigurationChange(item)}
              itemToString={(item: Configuration) => (item ? item.name : '')}
            />
          </FlexBox>
          <FlexBox fullWidth direction="row" margin="6 6 6" align="center" justifyContent="space-between">
            <Typography fontSize={16} fontWeight={600} sx={{ padding: '0 0 0 24px' }}>
              Area
            </Typography>
            <SelectDropdown
              placeholder="Select an Area"
              items={getSortedList(areas, 'name')}
              selectedItem={selectedArea as Area}
              onChange={(item: Area) => onAreaChange(item)}
              itemToString={(item: Area) => (item ? item.name : '')}
            />
          </FlexBox>
          <MuiBox className={classes.createClientName}>
            <InputField
              label="macAddress"
              size="medium"
              fullWidth
              placeholder="00:00:00:00:00:00"
              value={form.macAddress}
              onChange={(e) => onInputChange('macAddress', e)}
              onBlur={(e) => onInputBlur('macAddress', e, e.target.value.trim())}
              helperText={validate('macAddress')}
              error={validation.macAddress === INVALID}
            />
          </MuiBox>
        </Grid>

        {error && <Typography className={classes.createClientErrorText}>{error}</Typography>}

        {/* FORM SUBMIT */}
        <MuiBox className={classes.createClientButtonBox}>
          <Button
            disabled={buttonDisabled}
            customSize="large"
            onClick={onCreateClick}
            isLoading={loading}
            width={334}
            type="submit"
            className={classes.createClientButton}
          >
            Create Kiosk
          </Button>
        </MuiBox>
      </FlexBox>
    </FlexBox>
  );
};
